import React from 'react';
import {FormControl, MenuItem, Select, Tooltip, Typography} from '@mui/material';

/** Trend-window selection for period-over-period analysis, shared across the
 *  IRP workbook analysis tabs. `'midpoint'` splits the date range in half;
 *  numeric values are a recent-window length in months; `'allTime'` disables the
 *  split (all values cumulative). */
export type TrendWindow = number | 'midpoint' | 'allTime';

export const TREND_WINDOW_OPTIONS: ReadonlyArray<TrendWindow> = ['midpoint', 3, 6, 12, 18, 24, 36, 'allTime'];

export const DEFAULT_TREND_WINDOW: TrendWindow = 'midpoint';

/** Human-readable label for a trend-window value, e.g. 'Mid-point', 'All time', '12 mo'. */
export const formatTrendWindow = (w: TrendWindow): string =>
    w === 'midpoint' ? 'Mid-point' : w === 'allTime' ? 'All time' : `${w} mo`;

interface TrendWindowSelectProps {
    value: TrendWindow;
    onChange: (value: TrendWindow) => void;
    /** Tooltip text on the caption — wording differs per tab, so it is required. */
    tooltip: string;
    /** Caption text shown before the dropdown. */
    label?: string;
    minWidth?: number;
}

/** Italic caption + dropdown for picking the trend window. Stateless — the parent
 *  owns the value. Used by Barrier Effectiveness and Report Clustering. */
export const TrendWindowSelect: React.FC<TrendWindowSelectProps> = ({
    value,
    onChange,
    tooltip,
    label = 'Trend window',
    minWidth = 100,
}) => (
    <>
        <Tooltip title={tooltip} arrow>
            <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic', cursor: 'help'}}>
                {label}
            </Typography>
        </Tooltip>
        <FormControl size="small" sx={{minWidth, ml: 1}}>
            <Select
                value={value}
                onChange={(e): void => {
                    const v = e.target.value;
                    onChange(v === 'midpoint' || v === 'allTime' ? v : Number(v));
                }}
                sx={{fontSize: '0.75rem', height: 28, '& .MuiSelect-select': {py: 0, px: 1}}}
            >
                {TREND_WINDOW_OPTIONS.map((m) => (
                    <MenuItem key={String(m)} value={m} sx={{fontSize: '0.75rem'}}>
                        {formatTrendWindow(m)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    </>
);
